<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">{{ readOnly ? 'View User Groups' : 'View/Manage User Groups' }}</span>
        <v-btn icon="mdi-close" variant="text" @click="close"></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      
      <v-card-text class="pa-0">
        <v-row no-gutters style="min-height: 400px;">
          <!-- Left Panel: Groups List -->
          <v-col cols="5" class="border-e">
            <div class="pa-4">
              <div class="text-subtitle-2 mb-3">GROUPS</div>
              
              <!-- Search Groups -->
              <v-text-field
                v-model="groupSearch"
                label="Search groups..."
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-magnify"
                clearable
                autocomplete="off"
                class="mb-2"
                hide-details
              ></v-text-field>
              
              <!-- Add New Group (Admin only) -->
              <v-text-field
                v-if="!readOnly"
                v-model="newGroupName"
                label="Add New Group"
                variant="outlined"
                density="compact"
                append-inner-icon="mdi-plus"
                autocomplete="off"
                @click:append-inner="addGroup"
                @keyup.enter="addGroup"
                :error-messages="groupError"
                class="mb-3"
                hide-details="auto"
              ></v-text-field>
              
              <!-- Groups List -->
              <v-list density="compact" class="pa-0">
                <v-list-item
                  v-for="group in filteredGroups"
                  :key="group.name"
                  :active="selectedGroup?.name === group.name"
                  @click="selectGroup(group)"
                  class="rounded mb-1"
                  :class="{ 'bg-primary-lighten-4': selectedGroup?.name === group.name }"
                >
                  <template v-slot:prepend>
                    <v-icon>mdi-account-group</v-icon>
                  </template>
                  
                  <!-- Display Mode -->
                  <v-list-item-title>{{ group.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ group.userCount }} user(s)</v-list-item-subtitle>
                  
                  <template v-slot:append>
                    <div v-if="!readOnly && !group.isDefault">
                      <v-btn
                        icon="mdi-delete"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click.stop="deleteGroup(group)"
                        :disabled="group.userCount > 0"
                      ></v-btn>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </div>
          </v-col>
          
          <!-- Right Panel: Users Assignment -->
          <v-col cols="7">
            <div class="pa-4">
              <div class="d-flex justify-space-between align-center mb-3">
                <div class="text-subtitle-2">
                  <template v-if="selectedGroup">
                    {{ readOnly ? 'USERS IN' : 'MANAGE USERS IN' }}: {{ selectedGroup.name }}
                    <span class="text-caption text-medium-emphasis ml-2">
                      ({{ assignedUserIds.size }} assigned)
                    </span>
                  </template>
                  <template v-else>
                    Select a group
                  </template>
                </div>
                <v-btn
                  v-if="!readOnly && selectedGroup && hasChanges"
                  color="primary"
                  size="small"
                  @click="saveAssignments"
                  :loading="saving"
                >
                  Save Changes
                </v-btn>
              </div>
              
              <div v-if="!selectedGroup" class="text-center text-medium-emphasis pa-8">
                <v-icon size="64" class="mb-4">mdi-arrow-left</v-icon>
                <div>Select a group to {{ readOnly ? 'view' : 'manage' }} users</div>
              </div>
              
              <div v-else-if="loading" class="text-center pa-8">
                <v-progress-circular indeterminate></v-progress-circular>
              </div>
              
              <div v-else>
                <!-- Helper text for non-readonly mode -->
                <div v-if="!readOnly" class="text-caption text-medium-emphasis mb-2">
                  <template v-if="selectedGroup?.name === 'ALL USERS'">
                    ALL USERS is a virtual group showing all active users (read-only).
                  </template>
                  <template v-else>
                    Check users to add them to this group. Uncheck to remove.
                  </template>
                </div>
                
                <!-- User Search -->
                <v-text-field
                  v-model="userSearch"
                  label="Search users..."
                  variant="outlined"
                  density="compact"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  hide-details
                  class="mb-3"
                ></v-text-field>
                
                <!-- Users List -->
                <v-list density="compact" class="pa-0" style="max-height: 280px; overflow-y: auto;">
                  <v-list-item
                    v-for="user in filteredUsers"
                    :key="user.user_id"
                    class="rounded mb-1"
                  >
                    <template v-slot:prepend>
                      <v-checkbox
                        v-if="selectedGroup?.name !== 'ALL USERS'"
                        :model-value="isUserInGroup(user)"
                        @update:model-value="toggleUser(user)"
                        hide-details
                        density="compact"
                        class="mt-0"
                        :disabled="readOnly"
                      ></v-checkbox>
                    </template>
                    
                    <v-list-item-title>
                      {{ user.first_name }} {{ user.last_name }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      @{{ user.username }}
                      <template v-if="Array.isArray(user.user_group_name)">
                        <v-chip
                          v-for="(group, idx) in user.user_group_name.filter(g => g !== 'ALL USERS')"
                          :key="idx"
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          class="ml-1"
                        >
                          {{ group }}
                        </v-chip>
                      </template>
                      <v-chip
                        v-else-if="user.user_group_name && user.user_group_name !== 'ALL USERS'"
                        size="x-small"
                        color="primary"
                        variant="tonal"
                        class="ml-2"
                      >
                        {{ user.user_group_name }}
                      </v-chip>
                    </v-list-item-subtitle>
                    
                    <template v-slot:append>
                      <v-chip
                        v-if="user.has_sip"
                        size="x-small"
                        color="success"
                        variant="tonal"
                      >
                        <v-icon start size="x-small">mdi-phone</v-icon>
                        SIP
                      </v-chip>
                      <v-chip
                        v-else
                        size="x-small"
                        color="warning"
                        variant="tonal"
                      >
                        <v-icon start size="x-small">mdi-phone-off</v-icon>
                        No SIP
                      </v-chip>
                    </template>
                  </v-list-item>
                </v-list>
                
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AdminService from "@/services/adminService";

export default {
  name: "UserGroupsModal",
  
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ["update:modelValue", "groupsUpdated"],
  
  data() {
    return {
      groups: [],
      allUsers: [],
      selectedGroup: null,
      newGroupName: "",
      groupError: "",
      editingGroup: null,
      editGroupName: "",
      groupSearch: "",
      userSearch: "",
      loading: false,
      saving: false,
      // Track which users are assigned to the selected group
      assignedUserIds: new Set(),
      originalAssignedUserIds: new Set()
    };
  },
  
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      }
    },
    
    clientId() {
      return this.$store.getters.clientId;
    },
    
    filteredUsers() {
      if (!this.userSearch) {
        return this.allUsers;
      }
      const search = this.userSearch.toLowerCase();
      return this.allUsers.filter(u => 
        u.first_name.toLowerCase().includes(search) ||
        u.last_name.toLowerCase().includes(search) ||
        u.username.toLowerCase().includes(search)
      );
    },
    
    filteredGroups() {
      if (!this.groupSearch) {
        return this.groups;
      }
      const search = this.groupSearch.toLowerCase();
      return this.groups.filter(g => 
        g.name.toLowerCase().includes(search)
      );
    },
    
    hasChanges() {
      if (this.assignedUserIds.size !== this.originalAssignedUserIds.size) {
        return true;
      }
      for (const id of this.assignedUserIds) {
        if (!this.originalAssignedUserIds.has(id)) {
          return true;
        }
      }
      return false;
    }
  },
  
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.loadData();
      } else {
        this.resetState();
      }
    }
  },
  
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const [groupsRes, usersRes] = await Promise.all([
          AdminService.getUserGroups(this.clientId),
          AdminService.getAllUsersWithGroups(this.clientId)
        ]);
        this.groups = groupsRes.data;
        this.allUsers = usersRes.data;
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        this.loading = false;
      }
    },
    
    resetState() {
      this.selectedGroup = null;
      this.newGroupName = "";
      this.groupError = "";
      this.editingGroup = null;
      this.userSearch = "";
      this.assignedUserIds = new Set();
      this.originalAssignedUserIds = new Set();
    },
    
    selectGroup(group) {
      this.selectedGroup = group;
      this.userSearch = "";
      
      // Load users assigned to this group
      if (group.name === 'ALL USERS') {
        // Special case: ALL USERS shows everyone
        this.assignedUserIds = new Set(this.allUsers.map(u => u.user_id));
      } else {
        // Regular group: check if user's groups array includes this group
        const usersInGroup = this.allUsers.filter(u => {
          // Handle both array (new comma-separated format) and string (legacy)
          if (Array.isArray(u.user_group_name)) {
            return u.user_group_name.includes(group.name);
          }
          return u.user_group_name === group.name;
        });
        
        this.assignedUserIds = new Set(usersInGroup.map(u => u.user_id));
      }
      
      this.originalAssignedUserIds = new Set(this.assignedUserIds);
    },
    
    isUserInGroup(user) {
      return this.assignedUserIds.has(user.user_id);
    },
    
    toggleUser(user) {
      if (this.assignedUserIds.has(user.user_id)) {
        this.assignedUserIds.delete(user.user_id);
      } else {
        this.assignedUserIds.add(user.user_id);
      }
      // Force reactivity
      this.assignedUserIds = new Set(this.assignedUserIds);
    },
    
    async addGroup() {
      if (!this.newGroupName.trim()) {
        this.groupError = "Group name is required";
        return;
      }
      
      try {
        this.groupError = "";
        await AdminService.createUserGroup(this.clientId, this.newGroupName.trim());
        this.newGroupName = "";
        // Reload data to get accurate user counts
        await this.loadData();
        this.$emit("groupsUpdated");
      } catch (error) {
        this.groupError = error.response?.data?.message || "Error creating group";
      }
    },
    
    
    async deleteGroup(group) {
      // Build confirmation message
      let confirmMessage = `Are you sure you want to delete the group "${group.name}"?`;
      if (group.userCount > 0) {
        confirmMessage += `\n\n⚠️ ${group.userCount} user(s) will be automatically moved to "ALL USERS".`;
      }
      
      if (!confirm(confirmMessage)) {
        return;
      }
      
      try {
        const response = await AdminService.deleteUserGroup(this.clientId, group.name);
        
        // Show success message if users were reassigned
        if (response.data.groups) {
          if (response.data.reassignedUsers > 0) {
            alert(response.data.message);
          }
        }
        
        if (this.selectedGroup?.name === group.name) {
          this.selectedGroup = null;
        }
        
        // Reload data to get accurate user counts
        await this.loadData();
        
        this.$emit("groupsUpdated");
      } catch (error) {
        // Check if this is an IVR conflict error with detailed info
        const errorData = error.response?.data;
        if (errorData?.ivrConfigs && errorData.ivrConfigs.length > 0) {
          // Build a detailed error message with IVR info
          let errorMsg = `Cannot delete group "${group.name}"\n\n`;
          errorMsg += `⚠️ The following IVR configuration(s) are using SIP endpoints from users in this group:\n\n`;
          
          errorData.ivrConfigs.forEach((ivr, index) => {
            const status = ivr.isConfirmed ? '✅ Active' : '📝 Draft';
            const phones = ivr.phoneNumbers?.length > 0 ? ivr.phoneNumbers.join(', ') : 'No phone numbers';
            errorMsg += `${index + 1}. "${ivr.name}" (${status})\n`;
            errorMsg += `   Phone(s): ${phones}\n`;
          });
          
          errorMsg += `\nPlease update or remove these IVR configurations before deleting this group.`;
          alert(errorMsg);
        } else {
          alert(errorData?.message || "Error deleting group");
        }
      }
    },
    
    async saveAssignments() {
      if (!this.selectedGroup) return;
      
      // Prevent saving to ALL USERS (it's read-only virtual group)
      if (this.selectedGroup.name === 'ALL USERS') {
        alert('ALL USERS is a virtual group. Users are automatically in ALL USERS regardless of their specific group assignment.');
        return;
      }
      
      this.saving = true;
      try {
        await AdminService.updateGroupUsers(
          this.clientId,
          this.selectedGroup.name,
          Array.from(this.assignedUserIds)
        );
        
        // Reload both groups (for counts) and users (for updated group assignments)
        const [groupsRes, usersRes] = await Promise.all([
          AdminService.getUserGroups(this.clientId),
          AdminService.getAllUsersWithGroups(this.clientId)
        ]);
        
        this.groups = groupsRes.data;
        this.allUsers = usersRes.data;
        
        // Update selected group reference and assignedUserIds based on fresh data
        this.selectedGroup = this.groups.find(g => g.name === this.selectedGroup.name);
        
        // Recalculate assignedUserIds based on fresh user data
        this.assignedUserIds = new Set(
          this.allUsers
            .filter(u => {
              if (Array.isArray(u.user_group_name)) {
                return u.user_group_name.includes(this.selectedGroup.name);
              }
              return u.user_group_name === this.selectedGroup.name;
            })
            .map(u => u.user_id)
        );
        this.originalAssignedUserIds = new Set(this.assignedUserIds);
        
        this.$emit("groupsUpdated");
      } catch (error) {
        alert(error.response?.data?.message || "Error saving assignments");
      } finally {
        this.saving = false;
      }
    },
    
    close() {
      if (this.hasChanges) {
        if (!confirm("You have unsaved changes. Are you sure you want to close?")) {
          return;
        }
      }
      this.dialog = false;
    }
  }
};
</script>

<style scoped>
.border-e {
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
